import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { SaveBar } from "../../../components/utils/SaveBar";
import {
  fetchWorkOrder,
  fetchWorkOrderChecklist,
  updateWorkOrderChecklist,
} from "../../../api/work-order";
import {
  BuildingOfficeIcon,
  ClipboardDocumentIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import ChecklistItemLinesCompactV2 from "../../../components/dashboard/quality/checklists/checklistItemLinesCompactV2";
import ChecklistStickyFillChrome from "../../../components/dashboard/quality/checklists/ChecklistStickyFillChrome";
import ChecklistGapsBar from "../../../components/dashboard/quality/checklists/ChecklistGapsBar";
import { collectChecklistGaps } from "../../../components/dashboard/quality/checklists/checklistResponseConfig";
import { PMNotice } from "../../../components/utils/notifications";
import { useWorkOrderPermissions } from "../../../routes/roleOnly";
import { useProjectWorkOrdersAccess } from "../../../routes/roleOnly";

export const WorkOrderChecklistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);

  const [workOrder, setWorkOrder] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);

  const permissions = useWorkOrderPermissions(workOrder);
  const moduleAccess = useProjectWorkOrdersAccess(workOrder?.project);
  const woChecklistActive =
    Boolean(workOrder?.uses_checklist) || Boolean(workOrder?.checklist_id);
  const canEdit =
    Boolean(permissions?.canEdit) &&
    moduleAccess.canEdit &&
    woChecklistActive;

  const isAdmin = useMemo(
    () => currentUser?.roles?.some((r) => r.name === "admin") || false,
    [currentUser?.roles]
  );

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(false);
    fetchWorkOrder(id, { fields: "*" })
      .then((res) => {
        const wo = res?.data?.data ?? res?.data;
        setWorkOrder(wo);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(t("work_orders.checklist.load_work_order_error"));
        setError(true);
        setIsLoading(false);
      });
  }, [id, t]);

  useEffect(() => {
    if (!workOrder?.id || !woChecklistActive) {
      setIsLoadingChecklist(false);
      setChecklist(null);
      return;
    }
    if (!workOrder?.checklist_id) {
      setChecklist(null);
      setIsLoadingChecklist(false);
      return;
    }

    setIsLoadingChecklist(true);
    setApiErrors([]);
    fetchWorkOrderChecklist(workOrder.id, {
      fields:
        "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
    })
      .then((res) => {
        const data = res?.data;
        if (data?.errors) {
          setApiErrors(Array.isArray(data.errors) ? data.errors : [String(data.errors)]);
          setChecklist(null);
          setIsLoadingChecklist(false);
          return;
        }
        if (data?.id) {
          setChecklist({
            ...data,
            items: Array.isArray(data.items) ? data.items : [],
          });
        } else {
          setChecklist(null);
        }
        setIsLoadingChecklist(false);
      })
      .catch(() => {
        setApiErrors([t("work_orders.checklist.load_error")]);
        setChecklist(null);
        setIsLoadingChecklist(false);
      });
  }, [workOrder?.id, workOrder?.uses_checklist, workOrder?.checklist_id, t]);

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors.length ? errors : ["Error"]} />;
  }

  if (!workOrder?.id) {
    return <ResourceEmpty />;
  }

  if (!woChecklistActive) {
    return (
      <DashboardHeader
        variant="v2"
        title={workOrder.displayTitle || workOrder.title}
        subtitle={t("work_orders.checklist.page_subtitle")}
      >
        <div className="mx-auto max-w-3xl px-4 py-8">
          <PMNotice
            title={t("work_orders.checklist.disabled_title")}
            description={t("work_orders.checklist.disabled_body")}
          />
        </div>
        <SaveBar
          alwaysShow
          onBack={() => navigate(`/dashboard/work_orders/${workOrder.id}`)}
          backLabel={t("work_orders.checklist.back_to_work_order")}
        />
      </DashboardHeader>
    );
  }

  const gapSummary = collectChecklistGaps(checklist?.items || []);
  const compactProgress =
    gapSummary.totalCount > 0
      ? `${gapSummary.resolvedCount}/${gapSummary.totalCount}`
      : null;
  const compactStatus = !checklist?.id
    ? t("quality.checklist.gaps.missing")
    : gapSummary.gaps.length === 0
    ? t("quality.checklist.gaps.statusDone")
    : t("quality.checklist.gaps.statusOpen");

  const headerMeta = [
    {
      label: workOrder?.customer?.displayTitle || workOrder?.customer?.name,
      path: workOrder?.customer?.id
        ? `/dashboard/customers/${workOrder.customer.id}`
        : null,
      icon: UsersIcon,
      hidden: !workOrder?.customer?.id,
    },
    {
      label: workOrder?.project?.displayTitle,
      path: workOrder?.project?.id
        ? `/dashboard/projects/${workOrder.project.id}`
        : null,
      icon: BuildingOfficeIcon,
      hidden: !workOrder?.project?.id,
    },
  ].filter((x) => !x.hidden);

  return (
    <>
      <ChecklistStickyFillChrome
        title={workOrder.displayTitle || workOrder.title}
        subtitle={t("work_orders.checklist.page_subtitle")}
        backTo={`/dashboard/work_orders/${workOrder.id}`}
        progressLabel={compactProgress}
        statusLabel={compactStatus}
        items={checklist?.items || []}
      />
      <DashboardHeader
        variant="v2"
        title={workOrder.displayTitle || workOrder.title}
        subtitle={t("work_orders.checklist.page_subtitle")}
        meta={headerMeta}
        chromeClassName="hidden xl:block"
      >
        <div className="mx-auto max-w-5xl px-2 py-4 sm:px-4">
          <div className="sticky top-[var(--dashboard-topbar-height,4rem)] z-20 mb-4 hidden xl:block">
            <div className="overflow-hidden bg-white shadow sm:rounded-lg">
              <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-4 sm:px-6">
                <ClipboardDocumentIcon className="h-6 w-6 text-teal-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("work_orders.checklist.page_heading")}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {workOrder.number} — {workOrder.title}
                  </p>
                </div>
              </div>
              {checklist?.items?.length > 0 ? (
                <div className="px-2 pb-1.5 pt-1 sm:px-3">
                  <ChecklistGapsBar items={checklist.items} position="static" />
                </div>
              ) : null}
            </div>
          </div>

          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-6 sm:px-6">
              {!canEdit && (
                <div className="mb-6 rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                  {t("work_orders.checklist.read_only_banner")}
                </div>
              )}

              {isLoadingChecklist ? (
                <div className="flex items-center justify-center py-12">
                  <ResourceLoading />
                  <span className="ml-3 text-gray-600">
                    {t("work_orders.checklist.loading")}
                  </span>
                </div>
              ) : !checklist?.id ? (
                <PMNotice
                  title={t("work_orders.checklist.missing_title")}
                  description={t("work_orders.checklist.create_from_edit")}
                />
              ) : (
                <ChecklistItemLinesCompactV2
                  checklist={checklist}
                  items={checklist.items || []}
                  reportId={workOrder.id}
                  editable={canEdit && checklist.is_items_editable}
                  disabled={
                    !canEdit ||
                    (checklist.is_items_editable !== undefined &&
                      !checklist.is_items_editable)
                  }
                  isAdmin={isAdmin}
                  validationErrors={{}}
                  updateChecklistItems={async (woId, payload) => {
                    return await updateWorkOrderChecklist(woId, payload);
                  }}
                  onItemsUpdate={(updatedItems) => {
                    setChecklist((c) =>
                      c ? { ...c, items: updatedItems } : c
                    );
                  }}
                  showGapsBar={false}
                />
              )}

              {apiErrors.length > 0 ? (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <ul className="list-inside list-disc">
                    {apiErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DashboardHeader>

      <SaveBar
        alwaysShow
        onBack={() => navigate(`/dashboard/work_orders/${workOrder.id}`)}
        backLabel={t("work_orders.checklist.back_to_work_order")}
      />
    </>
  );
};
